/**
 * Panel state context — controls the sidebar panel (collapsed/expanded, active tab).
 *
 * Previously also managed external assets (ExternalAsset[]). Assets are now
 * detected purely from tool call results in MessageArtifacts. This context
 * is kept for panel state only.
 *
 * The provider also tracks the host widget's current width via
 * GAIA_EMBED_STATE messages from embed.js, so opening the panel from any
 * caller can request a resize when the widget is too narrow.
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

// Below this widget width we don't have room to show the inner side panel
// (Files / Tasks) next to the chat. The widget will auto-resize to at least
// this many pixels when the user opens the side panel.
export const SIDE_PANEL_MIN_WIDGET_WIDTH = 850;

/* ─── Context ────────────────────────────────────────────────────── */

type PanelContextValue = {
  /** Whether the sidebar panel is collapsed. */
  panelCollapsed: boolean;
  /** Set panel collapsed state. When opening from a too-narrow widget,
   * automatically requests a parent resize via GAIA_EMBED_RESIZE. */
  setPanelCollapsed: (collapsed: boolean) => void;
  /** Active tab in the sidebar panel ("files" | "assets"). */
  panelTab: string;
  setPanelTab: (tab: string) => void;
  /** Whether a sidebar panel is available (ChatLayout is mounted). */
  hasSidebarPanel: boolean;
  setHasSidebarPanel: (v: boolean) => void;
  /** Current host widget width (px), reported by embed.js. */
  widgetWidth: number;
};

const PanelContext = createContext<PanelContextValue | null>(null);

export function ExternalAssetsProvider({ children }: { children: ReactNode }) {
  const [panelCollapsed, setPanelCollapsedRaw] = useState(false);
  const [panelTab, setPanelTab] = useState('files');
  const [hasSidebarPanel, setHasSidebarPanel] = useState(false);
  const [widgetWidth, setWidgetWidth] = useState<number>(window.innerWidth);

  // Track the host widget width via GAIA_EMBED_STATE messages from embed.js.
  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const data = event.data;
      if (data && data.type === 'GAIA_EMBED_STATE' && typeof data.width === 'number') {
        setWidgetWidth(data.width);
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Wrapper around setPanelCollapsed that handles widget resize when
  // opening the panel from a too-narrow state. Optimistically updates
  // widgetWidth so the auto-close effect (in App.tsx) doesn't fire during
  // the postMessage round-trip.
  const setPanelCollapsed = useCallback(
    (collapsed: boolean) => {
      if (!collapsed && widgetWidth < SIDE_PANEL_MIN_WIDGET_WIDTH) {
        window.parent.postMessage(
          { type: 'GAIA_EMBED_RESIZE', width: SIDE_PANEL_MIN_WIDGET_WIDTH },
          '*',
        );
        setWidgetWidth(SIDE_PANEL_MIN_WIDGET_WIDTH);
      }
      setPanelCollapsedRaw(collapsed);
    },
    [widgetWidth],
  );

  // Auto-close the panel when the widget gets too narrow.
  useEffect(() => {
    if (widgetWidth < SIDE_PANEL_MIN_WIDGET_WIDTH && !panelCollapsed) {
      setPanelCollapsedRaw(true);
    }
  }, [widgetWidth, panelCollapsed]);

  return (
    <PanelContext.Provider
      value={{
        panelCollapsed,
        setPanelCollapsed,
        panelTab,
        setPanelTab,
        hasSidebarPanel,
        setHasSidebarPanel,
        widgetWidth,
      }}
    >
      {children}
    </PanelContext.Provider>
  );
}

export function useExternalAssets() {
  const ctx = useContext(PanelContext);
  if (!ctx) throw new Error('useExternalAssets must be used within an ExternalAssetsProvider');
  return ctx;
}
